import { Controller } from "@hotwired/stimulus"

// FD-900: Comfortable / Compact row density for the work-item layouts (list, board,
// spreadsheet). Purely a per-browser display preference: the choice lives in
// localStorage and the CSS in application.tailwind.css does the rest, so there is no
// server round trip and nothing to cache-bust on the fragment-cached rows and cards.
//
// The attribute goes on <html>, not on the layout container. The container is inside
// <body>, which a broadcast refresh morphs back to the server markup, and idiomorph
// strips any attribute the server did not render — so a compact board would snap back
// to comfortable on the next refresh. Turbo morphs and swaps only <body>, so an
// attribute on <html> survives both refreshes and Drive visits. The rules are scoped
// to [data-density-scope] (the layout roots) so other pages that reuse the row and
// card partials are unaffected.
//
// The toolbar itself IS morphed, so the pressed state of the options is re-synced on
// `turbo:morph` (the same shape as bulk-select#refresh).
const DENSITIES = ["comfortable", "compact"]

export default class extends Controller {
  static targets = ["option"]
  static values = { storageKey: { type: String, default: "fd-work-items-density" } }

  connect() {
    this.sync()
  }

  choose(event) {
    const density = event.params.density
    if (!DENSITIES.includes(density)) return
    this.#write(density)
    this.#apply(density)
  }

  sync() {
    this.#apply(this.#read())
  }

  #apply(density) {
    document.documentElement.dataset.density = density
    this.optionTargets.forEach((option) => {
      const active = option.dataset.densityDensityParam === density
      option.setAttribute("aria-pressed", String(active))
      option.classList.toggle("border-brand", active)
      option.classList.toggle("text-on-surface", active)
    })
  }

  #read() {
    try {
      const stored = localStorage.getItem(this.storageKeyValue)
      return DENSITIES.includes(stored) ? stored : "comfortable"
    } catch {
      return "comfortable"
    }
  }

  #write(density) {
    try {
      localStorage.setItem(this.storageKeyValue, density)
    } catch {
      // A display preference is a nicety; never let a storage failure break the toolbar.
    }
  }
}
